import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Tag, Search, Sun, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/contexts/ThemeContext';
import Footer from '@/components/Footer';
import NotFound from './NotFound';
import axios from 'axios';

const API = `${window.location.origin}/api`;

const CATEGORY_LABELS = {
  tax: 'Car Tax',
  mot: 'MOT',
  buying: 'Buying a Car',
  safety: 'Safety',
  general: 'Guides',
};

const CATEGORY_COLORS = {
  tax: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  mot: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  buying: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  safety: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  general: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
};

function renderMarkdown(content) {
  if (!content) return null;

  return content.split('\n').map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <br key={i} />;

    // H2
    if (trimmed.startsWith('## ')) {
      return <h2 key={i} className="text-2xl font-bold text-gray-900 dark:text-white mt-8 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{trimmed.slice(3)}</h2>;
    }
    // H3
    if (trimmed.startsWith('### ')) {
      return <h3 key={i} className="text-xl font-semibold text-gray-900 dark:text-white mt-6 mb-3">{trimmed.slice(4)}</h3>;
    }
    // Table header
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      if (trimmed.includes('---')) return null; // skip separator
      const cells = trimmed.split('|').filter(c => c.trim());
      const isHeader = i > 0 && content.split('\n')[i + 1]?.includes('---');
      if (isHeader) {
        return (
          <div key={i} className="overflow-x-auto mt-4">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  {cells.map((c, j) => <th key={j} className="text-left py-2 px-3 text-gray-900 dark:text-white font-semibold">{c.trim()}</th>)}
                </tr>
              </thead>
            </table>
          </div>
        );
      }
      return (
        <div key={i} className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-gray-100 dark:border-gray-800">
                {cells.map((c, j) => <td key={j} className="py-2 px-3 text-gray-700 dark:text-gray-300">{c.trim()}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
      );
    }
    // List items
    if (trimmed.startsWith('- **') || trimmed.startsWith('- ')) {
      const text = trimmed.slice(2);
      return (
        <li key={i} className="text-gray-700 dark:text-gray-300 leading-relaxed ml-4 mb-1 list-disc">
          <span dangerouslySetInnerHTML={{ __html: formatInline(text) }} />
        </li>
      );
    }
    // Numbered list
    if (/^\d+\.\s/.test(trimmed)) {
      const text = trimmed.replace(/^\d+\.\s/, '');
      return (
        <li key={i} className="text-gray-700 dark:text-gray-300 leading-relaxed ml-4 mb-1 list-decimal">
          <span dangerouslySetInnerHTML={{ __html: formatInline(text) }} />
        </li>
      );
    }
    // CTA links → convert to button
    if (trimmed.startsWith('**[') && trimmed.includes('](/?ref=blog)')) {
      const linkText = trimmed.match(/\*\*\[(.*?)\]/)?.[1] || '';
      return (
        <div key={i} className="my-6">
          <Link to="/" className="inline-block bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold px-6 py-3 rounded-lg transition-colors text-sm" data-testid="blog-cta-btn">
            {linkText.replace(' →', '')}
          </Link>
        </div>
      );
    }
    // Affiliate recommendation cards
    if (trimmed.startsWith('>>>AFFILIATE:')) {
      const parts = trimmed.replace('>>>AFFILIATE:', '').split('|').map(s => s.trim());
      const [id, title, description, linkText] = parts;
      return (
        <div key={i} className="my-6 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/40 rounded-lg p-4" data-testid={`affiliate-${id}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-gray-900 dark:text-white font-semibold text-sm mb-1">{title}</p>
              <p className="text-gray-500 dark:text-gray-400 text-xs">{description}</p>
            </div>
            <a
              href={`#affiliate-${id}`}
              className="shrink-0 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold px-4 py-2 rounded-md transition-colors"
              data-testid={`affiliate-link-${id}`}
              rel="noopener sponsored"
            >
              {linkText || 'Learn more'}
            </a>
          </div>
          <p className="text-gray-400 dark:text-gray-600 text-[10px] mt-2">Ad · We may earn a commission</p>
        </div>
      );
    }
    // Regular paragraph
    return <p key={i} className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3" dangerouslySetInnerHTML={{ __html: formatInline(trimmed) }} />;
  });
}

function formatInline(text) {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900 dark:text-white font-semibold">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code class="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm">$1</code>');
}

function BlogArticle() {
  const { slug } = useParams();
  const { theme, toggleTheme } = useTheme();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    fetchPost();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(res.data);
      // Fetch related posts in same category
      const allRes = await axios.get(`${API}/blog/posts?category=${res.data.category}&limit=4`);
      setRelatedPosts(allRes.data.posts.filter(p => p.slug !== slug).slice(0, 3));
    } catch (err) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      } else {
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
      </div>
    );
  }

  if (notFound || !post) {
    return <NotFound />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      {/* Header */}
      <header className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 transition-colors">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/">
            <img
              src="https://customer-assets.emergentagent.com/job_carinfo-lookup/artifacts/kwmtzzvb_image.png"
              alt="IsItTaxed.co.uk"
              className="h-12 w-auto"
            />
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              data-testid="theme-toggle-btn"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <Link to="/">
              <Button size="sm" className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-semibold" data-testid="check-vehicle-btn">
                Check a Vehicle
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <article className="container mx-auto px-4 py-12 max-w-3xl">
        <Link to="/blog" className="text-gray-400 dark:text-gray-500 text-sm hover:text-gray-600 dark:hover:text-gray-300 transition-colors inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="h-3 w-3" /> All articles
        </Link>

        <div className="mb-8">
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${CATEGORY_COLORS[post.category] || CATEGORY_COLORS.general}`}>
            {CATEGORY_LABELS[post.category] || post.category}
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mt-4 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="article-title">
            {post.title}
          </h1>
          {post.meta_description && (
            <p className="text-gray-500 dark:text-gray-400 text-lg" data-testid="article-description">
              {post.meta_description}
            </p>
          )}
        </div>

        {/* Article content */}
        <div className="prose-custom" data-testid="article-content">
          {renderMarkdown(post.content)}
        </div>

        {/* CTA Banner */}
        <div className="mt-12 bg-gray-900 dark:bg-gray-800 rounded-xl p-8 text-center" data-testid="article-cta-banner">
          <h3 className="text-white text-xl font-bold mb-2">Check any UK vehicle — free, instant</h3>
          <p className="text-gray-400 mb-5 text-sm">Tax status, MOT, annual cost, emissions, and more. No sign-up needed.</p>
          <Link to="/">
            <Button className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold px-8" data-testid="article-cta-btn">
              <Search className="h-4 w-4 mr-2" /> Check a Vehicle Now
            </Button>
          </Link>
        </div>

        {/* Related articles */}
        {relatedPosts.length > 0 && (
          <div className="mt-12">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Related articles</h3>
            <div className="space-y-3">
              {relatedPosts.map(rp => (
                <Link key={rp.slug} to={`/blog/${rp.slug}`} className="block" data-testid={`related-${rp.slug}`}>
                  <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg hover:border-gray-300 dark:hover:border-gray-700 transition-colors">
                    <span className="text-gray-900 dark:text-white font-medium text-sm">{rp.title}</span>
                    <ArrowLeft className="h-4 w-4 text-gray-400 rotate-180 shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>

      <Footer />
    </div>
  );
}

export default BlogArticle;
